import { Button } from "@/components/ui/button";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { RefreshCw, Send, CheckCircle } from "lucide-react";
import { useUpdatePrescription } from "../../hooks/use-update-prescription";
import { useSendPrescription } from "../../hooks/use-send-prescription";
import { useUpdatePrescriptionTags } from "../../hooks/use-update-prescription-tags";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface PrescriptionActionsProps {
  prescriptionId: string;
  isSent: boolean;
  canSend: boolean;
  tags?: string[] | null;
}

export function PrescriptionActions({
  prescriptionId,
  isSent,
  canSend,
  tags,
}: PrescriptionActionsProps) {
  const canWrite = useHasAccess("write");
  const updateMutation = useUpdatePrescription();
  const sendMutation = useSendPrescription();
  const updateTagsMutation = useUpdatePrescriptionTags();

  const doctorDecisionOptions = [
    { value: "doctor_updated", label: "Updated by doctor" },
    {
      value: "waiting_for_doctor_update",
      label: "Waiting for doctor's update",
    },
    {
      value: "asked_pharmacy_for_drug_info",
      label: "Asked pharmacy for drug info",
    },
    {
      value: "sent_with_missing_meds",
      label: "Sent with missing med(s)",
    },
    { value: "user_document_pending", label: "User document pending" },
    { value: "no_show", label: "No Show" },
    { value: "no_prescription_needed", label: "No prescription needed" },
  ];
  const doctorDecisionTags = doctorDecisionOptions.map((option) => option.value);
  const selectedDecisionTag =
    tags?.find((tag) => doctorDecisionTags.includes(tag)) || "";
  const shouldDisableSendForDecision =
    selectedDecisionTag === "no_prescription_needed" ||
    selectedDecisionTag === "no_show";
  const isSendDisabled =
    !canSend || sendMutation.isPending || shouldDisableSendForDecision;

  const handleUpdate = () => {
    updateMutation.mutate(prescriptionId);
  };

  const handleSend = () => {
    if (shouldDisableSendForDecision) return;
    sendMutation.mutate(prescriptionId);
  };

  const handleDoctorDecisionChange = (value: string) => {
    const existingTags = tags ?? [];
    const tagsWithoutDoctorDecision = existingTags.filter(
      (tag) => !doctorDecisionTags.includes(tag),
    );
    const nextTags =
      value === "none"
        ? tagsWithoutDoctorDecision
        : [...tagsWithoutDoctorDecision, value];

    updateTagsMutation.mutate({
      prescriptionId,
      tags: nextTags,
    });
  };

  return (
    <div className="mt-4 flex gap-2 items-center">
      <HasAccess permission="write">
      <Button
        onClick={handleUpdate}
        disabled={updateMutation.isPending}
        variant="outline"
        size="sm"
      >
        <RefreshCw
          className={`h-4 w-4 mr-2 ${
            updateMutation.isPending ? "animate-spin" : ""
          }`}
        />
        Update Prescription Order
      </Button>
      </HasAccess>
      {isSent ? (
        <div className="flex items-center gap-1">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <span className="px-2 py-0.5 text-xs font-bold rounded bg-green-100 text-green-800">
            SENT
          </span>
        </div>
      ) : (
        <HasAccess permission="write">
        <Button
          onClick={handleSend}
          disabled={isSendDisabled}
          className="bg-orange-500 hover:bg-orange-600 text-white disabled:bg-gray-300 disabled:text-gray-600 disabled:hover:bg-gray-300"
          size="sm"
        >
          <Send
            className={`h-4 w-4 mr-2 ${
              sendMutation.isPending ? "animate-spin" : ""
            }`}
          />
          Send Prescription
        </Button>
        </HasAccess>
      )}
      <div className="ml-auto">
        <Select
          value={selectedDecisionTag || "waiting_for_doctor_update"}
          onValueChange={handleDoctorDecisionChange}
          disabled={updateTagsMutation.isPending || !canWrite}
        >
          <SelectTrigger className="w-[240px]">
            <SelectValue placeholder="Doctor decision" />
          </SelectTrigger>
          <SelectContent>
            {doctorDecisionOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
